import './styles.scss';

import * as React from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import * as PhoneInput from 'global_elements/Inputs/TextInput/lib/phoneNumber';
import * as EmailInput from 'global_elements/Inputs/TextInput/lib/email';
import DataListsProxy, { DataListType } from 'api/dataLists/dataListsProxy';
import PageLayout from 'global_elements/Layouts/PageLayout';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import TertiaryHeader from 'global_elements/Text/TertiaryHeader';
import InlineText from 'global_elements/Text/InlineText';
import Button from 'global_elements/Button';
import LabeledTextInput from 'global_elements/Inputs/TextInput/LabeledTextInput';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import MHODateTime from 'domain/dateTime/MHODateTime';
import { UserContext } from 'context/user';
import UserProxy from 'api/user/userProxy';
import ApiResponse from 'api/lib/models/ApiResponse';
import UserAccount from 'interfaces/users/userAccount';
import UserPatientRelationship from 'interfaces/users/userPatientRelationship';
import { UpdateAccountPatientRequest } from 'interfaces/patients/updateAccountPatientRequest';
import { CreateAccountRequest } from 'interfaces/facilityUsers/createAccountRequest';
import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import SecondaryHeader from 'global_elements/Text/SecondaryHeader';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { ButtonVariant } from 'global_elements/Button/variants';
import { SelectOption } from 'types/inputProps';
import { TextInputVariant } from 'global_elements/Inputs/TextInput/variants';
import { SingleStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { ListFacility } from 'interfaces/dataLists/listFacility';
import PatientUsersProxy from 'api/patientUsers/patientUsersProxy';
import PopupWindow from 'global_elements/Layouts/PopupWindow';
import { getMsalPolicies } from 'config/msal/configMgmt';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'constants/languages';
import { patientAccountTranslationKeys } from './i18n';
import DeleteConfirmationPopup from './DeleteConfirmationPopup';
import { AccountData, PatientData, deepEqual } from './types';
import FeatureFlagManager from '../../../config/featureFlags/FeatureFlagManager';
import { FeatureFlagName } from '../../../config/featureFlags';

const msalPolicies = getMsalPolicies();

const PatientAccountPage = (): React.JSX.Element => {
  const { user } = React.useContext(UserContext);
  const userAccountIdStr = user ? user.accountId : '-1';
  const userAccountId = parseInt(userAccountIdStr, 10);
  const [isLoadingRelationshipOptions, setIsLoadingRelationshipOptions] = React.useState<boolean>(true);
  const [isLoadingPatientAccount, setIsLoadingPatientAccount] = React.useState<boolean>(true);
  const [isLoadingPatient, setIsLoadingPatient] = React.useState<boolean>(true);
  const isLoading = isLoadingRelationshipOptions || isLoadingPatientAccount || isLoadingPatient;
  const [selectedTab, setSelectedTab] = React.useState<number>(0);
  const [relationshipOptions, setRelationshipOptions] = React.useState<SelectOption[]>([]);
  const [languageOptions, setLanguageOptions] = React.useState<SelectOption[]>([]);
  const [stateOptions, setStateOptions] = React.useState<SelectOption[]>([]);
  const [facilityOptions, setFacilityOptions] = React.useState<SelectOption[]>([]);
  const [facilities, setFacilities] = React.useState<ListFacility[]>([]);
  const [patientStays, setPatientStays] = React.useState<any[]>([]);
  const [showAddTreatmentPopup, setShowAddTreatmentPopup] = React.useState<boolean>(false);
  const [stayPin, setStayPin] = React.useState<string>('');
  const [stayPinError, setStayPinError] = React.useState<string>('');
  const [userPatientRelationship, setUserPatientRelationship] = React.useState<UserPatientRelationship>();
  const { instance } = useMsal();
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'patientAccount' });
  const [originalAccountData, setOriginalAccountData] = React.useState<AccountData>({
    accountPrimaryEmail: user?.name || '',
    accountFirstName: '',
    accountLastName: '',
    accountRelationship: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectARelationship), value: '' },
    accountAddress: '',
    accountCity: '',
    accountState: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectAState), value: '' },
    accountZip: '',
    accountPhone: '',
    accountNotificationEmail: '',
    accountReceiveEmails: false,
    accountReceiveTexts: false,
    accountUserRoleID: 0,
    accountID: 0,
    accountStatusID: 0,
    accountPatientID: 0,
    lastLoginDate: '',
    emailSentDate: '',
    preferredLanguage: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectALanguage), value: '' },
  });

  const [modifiedAccountData, setModifiedAccountData] = React.useState<AccountData>({
    accountPrimaryEmail: user?.name || '',
    accountFirstName: '',
    accountLastName: '',
    accountRelationship: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectARelationship), value: '' },
    accountAddress: '',
    accountCity: '',
    accountState: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectAState), value: '' },
    accountZip: '',
    accountPhone: '',
    accountNotificationEmail: '',
    accountReceiveEmails: false,
    accountReceiveTexts: false,
    accountUserRoleID: 0,
    accountID: 0,
    accountStatusID: 0,
    accountPatientID: 0,
    lastLoginDate: '',
    emailSentDate: '',
    preferredLanguage: { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectALanguage), value: '' },
  });

  const [originalPatientData, setOriginalPatientData] = React.useState<PatientData>({
    emergencyContactFirstName: '',
    emergencyContactLastName: '',
    emergencyContactRelationship: { label: t(patientAccountTranslationKeys.patientInfo.erContact.selectARelationship), value: '' },
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    facility: { label: t(patientAccountTranslationKeys.patientInfo.preferredFacility.selectAFacility), value: '' },
    providerName: '',
    providerPhone: '',
    providerWebAddress: '',
    patientID: 0,
    patientAccountID: 0,
  });

  const [modifiedPatientData, setModifiedPatientData] = React.useState<PatientData>({
    emergencyContactFirstName: '',
    emergencyContactLastName: '',
    emergencyContactRelationship: { label: t(patientAccountTranslationKeys.patientInfo.erContact.selectARelationship), value: '' },
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    facility: { label: t(patientAccountTranslationKeys.patientInfo.preferredFacility.selectAFacility), value: '' },
    providerName: '',
    providerPhone: '',
    providerWebAddress: '',
    patientID: 0,
    patientAccountID: 0,
  });
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const isLocalizationEnabled = FeatureFlagManager.get<boolean>(FeatureFlagName.isLocalizationEnabled);

  const handleKeyUp = (e: any, callback: any): void => {
    if (e.key === 'Enter' && callback) {
      callback();
    }
  };

  const handleDataChange = (target: keyof AccountData | keyof PatientData, value: string | SelectOption | boolean): void => {
    if (selectedTab === 0) {
      setModifiedAccountData((prevState) => ({
        ...prevState,
        [target]: value,
      }));
    } else {
      setModifiedPatientData((prevState) => ({
        ...prevState,
        [target]: value,
      }));
    }
  };

  const handleRelationshipSelect = (option: SelectOption): void => {
    if (selectedTab === 0) {
      handleDataChange('accountRelationship', option);
    } else {
      handleDataChange('emergencyContactRelationship', option);
    }
  };

  const handleStateSelect = (option: SelectOption): void => {
    handleDataChange('accountState', option);
  };

  const handleLanguageSelect = (option: SelectOption): void => {
    handleDataChange('preferredLanguage', option);
  };

  const handleFacilitySelect = (option: SelectOption): void => {
    const selectedFacility = facilities.find((f) => f.facilityID.toString() === option.value);
    const phone = selectedFacility && selectedFacility.phoneNumber ? selectedFacility.phoneNumber : '';
    const website = selectedFacility && selectedFacility.website ? selectedFacility.website : '';
    setModifiedPatientData((prevState) => ({
      ...prevState,
      facility: option,
      providerPhone: phone,
      providerWebAddress: website,
    }));
  };

  const getAccountPatientInfo = (patientId: number): void => {
    UserProxy.getUserPatientInfo(
      patientId,
      (accountResponse: ApiResponse<UserAccount[]>) => {
        if (accountResponse.data && accountResponse.data.length > 0) {
          const patient: UserAccount = accountResponse.data[accountResponse.data.length - 1];
          const facilityOption = facilityOptions.find((f) => f.value === patient.facilityID?.toString());
          const patientInfo = {
            emergencyContactFirstName: patient.contactFirstName,
            emergencyContactLastName: patient.contactLastName,
            emergencyContactRelationship: { label: patient.contactRelationship, value: patient.contactRelationshipID },
            emergencyContactPhone: patient.contactPhone,
            emergencyContactEmail: patient.contactEmail,
            facility: { label: patient.facilityName, value: patient.facilityID },
            providerName: patient.facilityID?.toString(),
            providerPhone: patient.facilityPhone,
            providerWebAddress: patient.facilityWebsite,
            patientAccountID: patient.patientAccountID,
            patientID: patient.patientID,
          };
          if (facilityOption) patientInfo.facility = { label: facilityOption.label, value: parseInt(facilityOption.value, 10) };
          setOriginalPatientData(patientInfo);
          setModifiedPatientData(patientInfo);
        }
        setIsLoadingPatient(false);
      },
      (errorResponse) => {
        console.log(errorResponse);
        setIsLoadingPatient(false);
      },
    );
  };

  const getUserPatientRelationshipInfo = (accountId: number, patientId: number): void => {
    UserProxy.getUserPatientRelationshipInfo(
      accountId,
      patientId,
      (resp: ApiResponse<UserPatientRelationship[]>) => {
        if (resp.data && resp.data.length) {
          const option = resp.data[0];
          const selectOption = { label: option.relationship, value: option.relationshipID.toString() };
          setUserPatientRelationship(option);
          setOriginalAccountData((prevState) => ({
            ...prevState,
            accountRelationship: selectOption,
          }));
          setModifiedAccountData((prevState) => ({
            ...prevState,
            accountRelationship: selectOption,
          }));
          handleRelationshipSelect(selectOption);
        }
      },
      (err) => {
        console.log(err);
      },
    );
  };

  const handleAccountInfoResponse = (resp: any): void => {
    const acct: UserAccount = resp[0];
    const selectStateOption = { label: acct.state, value: acct.state };
    const selectLangOption = { label: acct.preferredLanguage, value: acct.preferredLanguageID?.toString() };
    const userAccount: any = {
      accountID: acct.accountID,
      accountPrimaryEmail: acct.accountEmail,
      accountFirstName: acct.firstName,
      accountLastName: acct.lastName,
      accountAddress: acct.address,
      accountCity: acct.city,
      accountState: selectStateOption,
      accountZip: acct.zipCode,
      accountPhone: acct.phone,
      accountNotificationEmail: acct.notificationEmail,
      accountReceiveEmails: acct.emailNotify,
      accountReceiveTexts: acct.textNotify,
      accountUserRoleID: acct.userRoleID,
      accountStatusID: acct.statusID,
      accountPatientID: acct.patientID,
      emailSentDate: acct.emailSentDate,
      lastLoginDate: acct.lastLoginDate,
      preferredLanguage: selectLangOption,
    };
    setOriginalAccountData(userAccount);
    setModifiedAccountData(userAccount);
    setIsLoadingPatientAccount(false);
    getUserPatientRelationshipInfo(acct.accountID, acct.patientID);
    getAccountPatientInfo(acct.patientID);
  };

  const getRelationships = (): void => {
    setRelationshipOptions([]);
    DataListsProxy.getOptionList(
      DataListType.Relationship,
      (response) => {
        const newRelationshipOptions: SelectOption[] = [];

        if (response.data) {
          response.data.forEach((option) => {
            newRelationshipOptions.push({
              label: option.lookupDesc,
              value: option.lookupID.toString(),
            });
          });
        }

        setRelationshipOptions(newRelationshipOptions);
        setIsLoadingRelationshipOptions(false);
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  };

  const getStates = (): void => {
    setStateOptions([]);
    DataListsProxy.getStateList(
      (response) => {
        const newStateOptions: SelectOption[] = [];
        if (response.data) {
          response.data.forEach((option) => {
            newStateOptions.push({
              label: option.stateDesc.toString(),
              value: option.stateDesc.toString(),
            });
          });
        }
        setStateOptions(newStateOptions);
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  };

  const getLanguages = (): void => {
    setLanguageOptions([]);
    DataListsProxy.getOptionList(
      DataListType.Language,
      (response) => {
        const newLanguageOptions: SelectOption[] = [];

        if (response.data) {
          response.data.forEach((option) => {
            newLanguageOptions.push({
              label: option.lookupDesc,
              value: option.lookupID.toString(),
            });
          });
        }

        setLanguageOptions(newLanguageOptions);
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  };

  const populateDropdowns = (): void => {
    getRelationships();
    getStates();
    if (isLocalizationEnabled) {
      getLanguages();
    }
  };

  const getUserAccountInfo = (): void => {
    setIsLoadingPatientAccount(true);
    UserProxy.getUserAccountInfo(
      userAccountId,
      (accountResponse: ApiResponse<UserAccount[]>) => {
        if (accountResponse.data && accountResponse.data.length > 0) {
          handleAccountInfoResponse(accountResponse.data);
        }
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  };

  React.useEffect(() => {
    populateDropdowns();
  }, []);

  React.useEffect(() => {
    DataListsProxy.getAllUserFacilities(
      (resp) => {
        if (resp.data && resp.data.length) {
          setFacilities(resp.data);
          setFacilityOptions(
            resp.data.map((f) => ({
              label: f.facilityName,
              value: f.facilityID.toString(),
            })),
          );
        }
      },
      (resp) => console.log(resp),
    );

    getUserAccountInfo();
  }, []);

  const getAndSetPatientStays = (): void => {
    PatientUsersProxy.getPatientStays(originalAccountData.accountPatientID)
      .then((resp) => {
        setPatientStays(resp);
      })
      .catch((errorResponse) => {
        console.log(errorResponse);
      });
  };

  React.useEffect(() => {
    getAndSetPatientStays();
  }, [originalAccountData]);

  const handleInputEvent = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.type === 'text' || e.target.type === 'email') {
      if (e.target.name !== 'accountPhone' && e.target.name !== 'emergencyContactPhone') {
        handleDataChange(e.target.name as keyof AccountData | keyof PatientData, e.target.value);
      } else if (PhoneInput.validPhoneInput(e)) {
        const maskedInput = PhoneInput.maskInputAsPhoneNumber(e);

        if (maskedInput !== undefined) {
          if (selectedTab === 0) {
            handleDataChange('accountPhone', maskedInput);
          } else {
            handleDataChange('emergencyContactPhone', maskedInput);
          }
        }
      }
    } else {
      handleDataChange(e.target.name as keyof AccountData | keyof PatientData, e.target.checked);
    }
  };

  const phoneError = (phone: string): string | undefined => {
    if (!PhoneInput.validPhoneNumber(phone)) {
      return t(patientAccountTranslationKeys.errors.invalidPhoneNumber);
    }
    return undefined;
  };

  const isDataModified = (): boolean => {
    if (selectedTab === 0) {
      if (!deepEqual(originalAccountData, modifiedAccountData)) {
        return true;
      }
    } else if (!deepEqual(originalPatientData, modifiedPatientData)) {
      return true;
    }

    return false;
  };

  const invalidAccountData = (): boolean => modifiedAccountData.accountFirstName === ''
    || modifiedAccountData.accountLastName === ''
    || modifiedAccountData.accountRelationship?.value === ''
    || !!EmailInput.inputError(modifiedAccountData.accountNotificationEmail)
    || !!phoneError(modifiedAccountData.accountPhone);

  const invalidPatientData = (): boolean => !!EmailInput.inputError(modifiedPatientData.emergencyContactEmail) || !!phoneError(modifiedPatientData.emergencyContactPhone);

  const disableSave = (): boolean => {
    if (selectedTab === 0) {
      return !isDataModified() || invalidAccountData();
    }

    return !isDataModified() || invalidPatientData();
  };

  const cancelChanges = (): void => {
    if (selectedTab === 0) {
      setModifiedAccountData(originalAccountData);
    } else {
      setModifiedPatientData(originalPatientData);
    }
  };

  const getUpdatedAccountInfo = (): CreateAccountRequest => {
    const acct = modifiedAccountData;
    return {
      AccountEmail: acct.accountPrimaryEmail,
      AccountID: acct.accountID ?? null,
      UserRoleID: acct.accountUserRoleID,
      FirstName: acct.accountFirstName,
      LastName: acct.accountLastName,
      StatusID: acct.accountStatusID,
      UpdatedBy: acct.accountPrimaryEmail,
      Phone: acct.accountPhone,
      PreferredLanguageID: isLocalizationEnabled ? parseInt(acct.preferredLanguage.value, 10) : undefined,
      Address: acct.accountAddress,
      RelationshipID: parseInt(acct.accountRelationship.value, 10),
      City: acct.accountCity,
      State: acct.accountState.value,
      ZipCode: acct.accountZip,
      NotificationEmail: acct.accountNotificationEmail,
      EmailNotify: acct.accountReceiveEmails,
      TextNotify: acct.accountReceiveTexts,
      EmailSentDate: acct.emailSentDate,
      LastLoginDate: acct.lastLoginDate || new MHODateTime(new Date()).getDateTimeForPost(),
    };
  };

  const getUpdatedAccountPatientInfo = (): UpdateAccountPatientRequest => {
    const acct = modifiedPatientData;
    const relationshipId = acct.emergencyContactRelationship.value === '0' ? null : parseInt(acct.emergencyContactRelationship.value, 10);
    const facilityValue = acct.facility.value ? Number(acct.facility.value) : -1;
    return {
      patientAccountID: acct.patientAccountID,
      patientID: originalAccountData.accountPatientID,
      contactFirstName: acct.emergencyContactFirstName,
      contactLastName: acct.emergencyContactLastName,
      contactRelationshipID: relationshipId,
      contactPhone: acct.emergencyContactPhone,
      contactEmail: acct.emergencyContactEmail,
      facilityID: facilityValue,
      statusID: 1,
    };
  };

  const getPatientStaysMarkup = (): any => {
    if (patientStays && patientStays.length) {
      return patientStays.map((stay: any) => (
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.START} extraClasses="patient-stays--container">
          <InlineText
            text={`${stay.dateAdmitted ? new MHODateTime(stay.dateAdmitted).getFormattedCalendarDateLocale() : 'N/A'} - ${stay.dateDischarged ? new MHODateTime(stay.dateDischarged).getFormattedCalendarDateLocale() : 'N/A'}`}
            fontColor={FontColors.LIGHT_GRAY}
            fontSize={FontSizes.REGULAR}
          />
          <InlineText text={stay.facilityName} fontColor={FontColors.LIGHT_GRAY} fontSize={FontSizes.REGULAR} />
        </FlexContainer>
      ));
    }
    return '';
  };

  const saveUserPatientRelationship = (): void => {
    const acct = modifiedAccountData;
    UserProxy.saveUserPatientRelationshipInfo(
      [
        {
          accountID: userPatientRelationship?.accountID,
          accountPatientID: userPatientRelationship?.accountPatientID,
          careID: userPatientRelationship?.careID,
          patientID: userPatientRelationship?.patientID,
          relationshipID: parseInt(acct.accountRelationship.value, 10),
          statusID: 1,
          UpdatedBy: user?.email,
        },
      ],
      (resp) => console.log(resp),
      (e) => console.log(e),
    );
  };

  const saveChanges = (): void => {
    if (selectedTab === 0) {
      UserProxy.postUserAccountInfo(
        [getUpdatedAccountInfo()],
        () => {
          setOriginalAccountData(modifiedAccountData);
          saveUserPatientRelationship();
          setLanguageOptions([]);
          setRelationshipOptions([]);
          setStateOptions([]);
          setTimeout(() => {
            i18n.changeLanguage(modifiedAccountData.preferredLanguage.value === Languages.Spanish.toString() ? 'es' : 'en');
            getUserAccountInfo();
            populateDropdowns();
          }, 300);
        },
        (resp) => console.log('Error', resp),
      );
    } else {
      console.log(getUpdatedAccountPatientInfo());
      UserProxy.postUserPatientInfo(
        [getUpdatedAccountPatientInfo()],
        () => setOriginalPatientData(modifiedPatientData),
        (resp) => console.log('Error', resp),
      );
    }
  };

  const linkPatientStay = (): void => {
    UserProxy.linkPatientStay(
      originalAccountData.accountID,
      stayPin,
      (resp) => {
        console.log(resp);
        if (resp && resp.data.toString() === 'No LinkStay Found') {
          setStayPinError(t(patientAccountTranslationKeys.errors.registrationPinNoMatch));
        } else {
          setShowAddTreatmentPopup(false);
          setOriginalPatientData(modifiedPatientData);
          getAndSetPatientStays();
        }
      },
      (e) => {
        console.log(e);
        setStayPinError(t(patientAccountTranslationKeys.errors.linkingStay));
      },
    );
  };

  const validPinString = (pin: string): boolean => new RegExp(/^\d{8}$/).test(pin);

  const pinError = (): string | undefined => {
    let errorMsg;
    if (stayPin !== '' && !validPinString(stayPin)) {
      errorMsg = t(patientAccountTranslationKeys.errors.pinInvalid);
    }

    return errorMsg;
  };

  const onTabSelect = (index: number, lastIndex: number): boolean => {
    if (index === lastIndex) {
      return false;
    }

    switch (index) {
      case 0: // Account Information
        setSelectedTab(0);
        break;
      case 1: // Patient Information
        setSelectedTab(1);
        break;
      default:
        break;
    }

    return true;
  };

  const getLinkStayPopup = (): React.JSX.Element => (
    <PopupWindow>
      <SecondaryHeader text={t(patientAccountTranslationKeys.patientInfo.previousTreatments.popup.title)} fontColor={FontColors.PRIMARY} marginBottomPx={16} marginTopPx={32} />
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.CENTER} justify={JustifyVariant.CENTER} extraClasses="first-time-account-setup__account-info link-stays--popup">
        <LabeledTextInput
          variant={TextInputVariant.PRIMARY}
          label={`${t(patientAccountTranslationKeys.patientInfo.previousTreatments.popup.registrationPin)} *`}
          type="text"
          name="stayInfoPin"
          value={stayPin}
          onChange={(e) => setStayPin(e.target.value.slice(0, 8))}
          error={pinError()}
        />
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.SPACE_EVENLY} extraClasses="first-time-account-setup__account-info">
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setShowAddTreatmentPopup(false);
              setStayPin('');
              setStayPinError('');
            }}
          >
            <InlineText text={t(patientAccountTranslationKeys.patientInfo.previousTreatments.popup.cancel)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              linkPatientStay();
            }}
            disabled={stayPin === '' || !!pinError()}
          >
            <InlineText text={t(patientAccountTranslationKeys.patientInfo.previousTreatments.popup.submit)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
        {stayPinError && <InlineText text={stayPinError} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} />}
      </FlexContainer>
    </PopupWindow>
  );

  const accountPanel = (): React.JSX.Element => (
    <>
      {showDeletePopup && <DeleteConfirmationPopup accountID={originalAccountData.accountID} patientID={originalAccountData.accountPatientID} onCancel={() => setShowDeletePopup(false)} />}
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__account__email">
        <TertiaryHeader text={t(patientAccountTranslationKeys.accountInfo.accountId.title)} fontColor={FontColors.PRIMARY} marginBottomPx={9} />
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.CENTER} justify={JustifyVariant.SPACE_BETWEEN} extraClasses="patient-account-settings-page__account__email__content">
          <InlineText text={modifiedAccountData.accountPrimaryEmail} fontColor={FontColors.LIGHT_GRAY} fontSize={FontSizes.REGULAR} />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              instance.loginRedirect({ authority: msalPolicies.authorities.forgotPassword.authority, scopes: [] });
            }}
          >
            <InlineText text={t(patientAccountTranslationKeys.accountInfo.accountId.changePassword)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </FlexContainer>
      <form>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__account__information">
          <TertiaryHeader text={t(patientAccountTranslationKeys.accountInfo.accountHolder.title)} fontColor={FontColors.PRIMARY} marginBottomPx={9} />
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__account__information__first-row"
          >
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={`${t(patientAccountTranslationKeys.accountInfo.accountHolder.firstName)} *`}
              type="text"
              name="accountFirstName"
              value={modifiedAccountData.accountFirstName}
              onChange={handleInputEvent}
              autoFocus
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={`${t(patientAccountTranslationKeys.accountInfo.accountHolder.lastName)} *`}
              type="text"
              name="accountLastName"
              value={modifiedAccountData.accountLastName}
              onChange={handleInputEvent}
            />
            {(!modifiedAccountData || (!modifiedAccountData.accountRelationship && relationshipOptions.length === 0)) && (
              <LabeledSingleSelect
                label={`${t(patientAccountTranslationKeys.accountInfo.accountHolder.relationshipToPatient)} *`}
                styles={SingleStandardDropdownStyles}
                options={[]}
                defaultValue={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                value={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                onSelection={handleRelationshipSelect}
              />
            )}
            {!!modifiedAccountData && !!modifiedAccountData.accountRelationship && relationshipOptions.length > 0 && (
              <LabeledSingleSelect
                label={`${t(patientAccountTranslationKeys.accountInfo.accountHolder.relationshipToPatient)} *`}
                styles={SingleStandardDropdownStyles}
                options={relationshipOptions}
                defaultValue={modifiedAccountData.accountRelationship}
                value={modifiedAccountData.accountRelationship}
                onSelection={handleRelationshipSelect}
              />
            )}
          </FlexContainer>
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__account__information__second-row"
          >
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.accountInfo.accountHolder.address)}
              type="text"
              name="accountAddress"
              value={modifiedAccountData.accountAddress}
              onChange={handleInputEvent}
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.accountInfo.accountHolder.city)}
              type="text"
              name="accountCity"
              value={modifiedAccountData.accountCity}
              onChange={handleInputEvent}
            />
            {/* ***added state dropdown */}
            {(stateOptions.length === 0 || isLoadingPatientAccount) && (
              <LabeledSingleSelect
                label={t(patientAccountTranslationKeys.accountInfo.accountHolder.state)}
                styles={SingleStandardDropdownStyles}
                options={[]}
                defaultValue={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                value={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                onSelection={handleStateSelect}
              />
            )}
            {stateOptions.length > 0 && !isLoadingPatientAccount && (
              <LabeledSingleSelect
                label={t(patientAccountTranslationKeys.accountInfo.accountHolder.state)}
                styles={SingleStandardDropdownStyles}
                options={stateOptions}
                defaultValue={modifiedAccountData.accountState ?? { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectAState), value: '' }}
                value={modifiedAccountData.accountState}
                onSelection={handleStateSelect}
              />
            )}
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.accountInfo.accountHolder.zipCode)}
              type="text"
              name="accountZip"
              value={modifiedAccountData.accountZip}
              onChange={handleInputEvent}
            />
          </FlexContainer>
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__account__information__third-row"
          >
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.accountInfo.accountHolder.phone)}
              type="text"
              name="accountPhone"
              value={modifiedAccountData.accountPhone}
              onChange={handleInputEvent}
              error={phoneError(modifiedAccountData.accountPhone)}
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.accountInfo.accountHolder.emailForNotifications)}
              type="email"
              name="accountNotificationEmail"
              value={modifiedAccountData.accountNotificationEmail}
              onChange={handleInputEvent}
              error={EmailInput.inputError(modifiedAccountData.accountNotificationEmail)}
            />
            <FlexContainer
              display={DisplayVariant.FLEX_COL}
              align={AlignVariant.START}
              justify={JustifyVariant.START}
              extraClasses="patient-account-settings-page__account__information__third-row__notifications"
            >
              <TertiaryHeader text={t(patientAccountTranslationKeys.accountInfo.accountHolder.notifications)} fontColor={FontColors.SECONDARY} marginBottomPx={12} />
              <FlexContainer
                display={DisplayVariant.FLEX_ROW}
                align={AlignVariant.CENTER}
                justify={JustifyVariant.START}
                extraClasses="patient-account-settings-page__account__information__third-row__notifications__toggles"
              >
                <span>
                  <InlineText text={t(patientAccountTranslationKeys.accountInfo.accountHolder.notificationsEmail)} fontColor={FontColors.LIGHT_GRAY} fontSize={FontSizes.LARGE} bold />
                  <label className="toggle">
                    <input
                      onKeyUp={(e) => handleKeyUp(e, () => setModifiedAccountData({ ...modifiedAccountData, accountReceiveEmails: !modifiedAccountData.accountReceiveEmails }))}
                      name="accountReceiveEmails"
                      type="checkbox"
                      checked={modifiedAccountData.accountReceiveEmails}
                      onChange={handleInputEvent}
                    />
                    <span className="slider" />
                  </label>
                </span>
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>

          {isLocalizationEnabled && (
            <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__account__information__language-dd">
              {(languageOptions.length === 0 || isLoadingPatientAccount) && (
                <LabeledSingleSelect
                  label={t(patientAccountTranslationKeys.accountInfo.accountHolder.preferredLanguage)}
                  styles={SingleStandardDropdownStyles}
                  options={[]}
                  defaultValue={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                  value={{ label: t(patientAccountTranslationKeys.accountInfo.accountHolder.loading), value: '' }}
                  onSelection={handleLanguageSelect}
                />
              )}
              {languageOptions.length > 0 && !isLoadingPatientAccount && (
                <LabeledSingleSelect
                  label={t(patientAccountTranslationKeys.accountInfo.accountHolder.preferredLanguage)}
                  styles={SingleStandardDropdownStyles}
                  options={languageOptions}
                  defaultValue={modifiedAccountData.preferredLanguage ?? { label: t(patientAccountTranslationKeys.accountInfo.accountHolder.selectALanguage), value: '' }}
                  value={modifiedAccountData.preferredLanguage}
                  onSelection={handleLanguageSelect}
                />
              )}
            </FlexContainer>
          )}
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__account__information__buttons">
            <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.END} extraClasses="patient-account-settings-page__account__information__buttons__save">
              <Button variant={ButtonVariant.INVISIBLE} onClick={() => setShowDeletePopup(true)}>
                <InlineText text={t(patientAccountTranslationKeys.accountInfo.accountHolder.deleteAccount)} fontColor={FontColors.PRIMARY} fontSize={FontSizes.REGULAR} bold underlined />
              </Button>
              <Button variant={ButtonVariant.SECONDARY} onClick={cancelChanges} disabled={!isDataModified()}>
                <InlineText text={t(patientAccountTranslationKeys.accountInfo.accountHolder.cancel)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
              <Button variant={ButtonVariant.PRIMARY} onClick={saveChanges} disabled={disableSave()}>
                <InlineText text={t(patientAccountTranslationKeys.accountInfo.accountHolder.saveChanges)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </form>
    </>
  );

  const patientPanel = (): React.JSX.Element => (
    <>
      <form>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__patient__emergency-contact">
          <TertiaryHeader text={t(patientAccountTranslationKeys.patientInfo.erContact.title)} fontColor={FontColors.PRIMARY} marginBottomPx={9} />
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__patient__emergency-contact__first-row"
          >
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.erContact.firstName)}
              type="text"
              name="emergencyContactFirstName"
              value={modifiedPatientData.emergencyContactFirstName}
              onChange={handleInputEvent}
              autoFocus
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.erContact.lastName)}
              type="text"
              name="emergencyContactLastName"
              value={modifiedPatientData.emergencyContactLastName}
              onChange={handleInputEvent}
            />
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label={t(patientAccountTranslationKeys.patientInfo.erContact.relationshipToPatient)}
              options={relationshipOptions}
              defaultValue={modifiedPatientData.emergencyContactRelationship}
              value={modifiedPatientData.emergencyContactRelationship}
              onSelection={handleRelationshipSelect}
            />
          </FlexContainer>
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__patient__emergency-contact__second-row"
          >
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.erContact.phoneNumber)}
              type="text"
              name="emergencyContactPhone"
              value={modifiedPatientData.emergencyContactPhone}
              onChange={handleInputEvent}
              error={phoneError(modifiedPatientData.emergencyContactPhone)}
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.erContact.emailAddress)}
              type="email"
              name="emergencyContactEmail"
              value={modifiedPatientData.emergencyContactEmail}
              onChange={handleInputEvent}
              error={EmailInput.inputError(modifiedPatientData.emergencyContactEmail)}
            />
          </FlexContainer>
        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__patient__emergency-contact">
          <TertiaryHeader text={t(patientAccountTranslationKeys.patientInfo.preferredFacility.title)} fontColor={FontColors.PRIMARY} marginBottomPx={9} />
          <FlexContainer
            display={DisplayVariant.FLEX_ROW}
            align={AlignVariant.START}
            justify={JustifyVariant.SPACE_BETWEEN}
            extraClasses="patient-account-settings-page__patient__emergency-contact__first-row"
          >
            <LabeledSingleSelect
              styles={SingleStandardDropdownStyles}
              label={t(patientAccountTranslationKeys.patientInfo.preferredFacility.facility)}
              options={facilityOptions}
              defaultValue={modifiedPatientData.facility as SelectOption}
              value={modifiedPatientData.facility as SelectOption}
              onSelection={handleFacilitySelect}
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.preferredFacility.phoneNumber)}
              type="text"
              disabled={1}
              name="providerPhone"
              value={modifiedPatientData.providerPhone}
              onChange={handleInputEvent}
            />
            <LabeledTextInput
              variant={TextInputVariant.PRIMARY}
              label={t(patientAccountTranslationKeys.patientInfo.preferredFacility.websiteAddress)}
              type="text"
              disabled={1}
              name="providerWebAddress"
              value={modifiedPatientData.providerWebAddress}
              onChange={handleInputEvent}
            />
          </FlexContainer>
        </FlexContainer>
        <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.END} extraClasses="patient-account-settings-page__patient__buttons">
          <Button variant={ButtonVariant.SECONDARY} onClick={cancelChanges} disabled={!isDataModified()}>
            <InlineText text={t(patientAccountTranslationKeys.patientInfo.preferredFacility.cancel)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
          <Button variant={ButtonVariant.PRIMARY} onClick={saveChanges} disabled={disableSave()}>
            <InlineText text={t(patientAccountTranslationKeys.patientInfo.preferredFacility.saveChanges)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </form>
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page__patient__treatments">
        <TertiaryHeader text={t(patientAccountTranslationKeys.patientInfo.previousTreatments.title)} fontColor={FontColors.PRIMARY} marginBottomPx={9} />
        <div className="patient-stays">{getPatientStaysMarkup()}</div>
      </FlexContainer>
      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.END} extraClasses="patient-account-settings-page__patient__add-treatment">
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={() => {
            setShowAddTreatmentPopup(true);
          }}
        >
          <InlineText text={t(patientAccountTranslationKeys.patientInfo.previousTreatments.addTreatment)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
        </Button>
      </FlexContainer>
    </>
  );

  return (
    <PageLayout
      layout={PageLayoutVariant.PADDED}
      testText="Patient Account Page"
      loadingTitle={isLoading ? t(patientAccountTranslationKeys.loadingTitle) : undefined}
      loadingText={isLoading ? t(patientAccountTranslationKeys.loadingText) : undefined}
    >
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-account-settings-page">
        <Tabs onSelect={onTabSelect}>
          <TabList>
            <Tab disabled={isDataModified()}>
              {selectedTab === 0 ? (
                <InlineText text={t(patientAccountTranslationKeys.accountInfo.title)} fontColor={FontColors.DARK} fontSize={FontSizes.LARGE} />
              ) : (
                <InlineText text={t(patientAccountTranslationKeys.accountInfo.title)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.LARGE} />
              )}
            </Tab>
            <Tab disabled={isDataModified()}>
              {selectedTab === 1 ? (
                <InlineText text={t(patientAccountTranslationKeys.patientInfo.title)} fontColor={FontColors.DARK} fontSize={FontSizes.LARGE} />
              ) : (
                <InlineText text={t(patientAccountTranslationKeys.patientInfo.title)} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.LARGE} />
              )}
            </Tab>
          </TabList>

          <TabPanel>{accountPanel()}</TabPanel>
          <TabPanel>{patientPanel()}</TabPanel>
        </Tabs>
        {showAddTreatmentPopup && getLinkStayPopup()}
      </FlexContainer>
    </PageLayout>
  );
};

export default PatientAccountPage;
